import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Bell, CheckCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "az önce";
  if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} sa önce`;
  return `${Math.floor(diff / 86400)} gün önce`;
}

export default function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    try {
      const r = await axios.get(`${API}/me/notifications`);
      setItems(r.data?.items || []);
      setUnread(r.data?.unread_count || 0);
    } catch {
      /* noop */
    }
  }, [user]);

  // Initial + polling
  useEffect(() => {
    if (!user) return undefined;
    fetchAll();
    const t = setInterval(fetchAll, 30000);
    return () => clearInterval(t);
  }, [user, fetchAll]);

  // Click outside
  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  if (!user) return null;

  const onClickItem = async (n) => {
    setOpen(false);
    if (!n.read) {
      try { await axios.post(`${API}/me/notifications/${n.id}/read`); } catch { /* noop */ }
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      setUnread((u) => Math.max(0, u - 1));
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await axios.post(`${API}/me/notifications/read-all`); } catch { /* noop */ }
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="relative inline-flex items-center justify-center h-10 w-10 rounded-xl bg-white border border-slate-200 hover:border-[#10B981] transition-colors"
        aria-label="Bildirimler"
        data-testid="notification-bell"
      >
        <Bell className="h-5 w-5 text-[#0F172A]" />
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center"
            data-testid="notification-badge"
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed left-2 right-2 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[360px] sm:max-w-[92vw] rounded-2xl bg-white border border-slate-100 pak-shadow-lg overflow-hidden z-50"
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <div>
              <p className="text-sm font-semibold text-[#0F172A]">Bildirimler</p>
              <p className="text-[11px] text-slate-500">
                {unread > 0 ? `${unread} okunmamış` : "Tümü okundu"}
              </p>
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAll}
                className="text-xs font-medium text-[#10B981] hover:text-[#0EA371] flex items-center gap-1"
                data-testid="notification-mark-all-btn"
              >
                <CheckCheck className="h-3.5 w-3.5" /> Tümünü okundu işaretle
              </button>
            )}
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="py-8 text-center text-sm text-slate-500" data-testid="notification-empty">
                Henüz bildiriminiz yok.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100" data-testid="notification-list">
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => onClickItem(n)}
                      className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors flex gap-3 items-start ${n.read ? "" : "bg-emerald-50/40"}`}
                      data-testid={`notification-item-${n.id}`}
                    >
                      <span
                        className={`mt-1.5 h-2 w-2 rounded-full flex-shrink-0 ${n.read ? "bg-slate-200" : "bg-[#10B981]"}`}
                        aria-hidden="true"
                      />
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm leading-snug ${n.read ? "font-medium text-slate-700" : "font-semibold text-[#0F172A]"}`}>
                          {n.title}
                        </p>
                        {n.body && <p className="text-xs text-slate-500 mt-0.5 leading-relaxed line-clamp-2">{n.body}</p>}
                        <p className="text-[11px] text-slate-400 mt-1">{timeAgo(n.created_at)}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
